import { OrganizationsAdminPage } from '@pages/Admin/Organizations/OrganizationsAdminPage';
import Databases from '@pages/Database';
import Grid from '@pages/Database/grid';
import { EditorWrapper } from '@pages/Editor/wrapper';
import { EnvironmentPage } from '@pages/Environment/EnvironmentPage';
import Home from '@pages/Home/home.page';
import AddNewProcess from '@pages/NewProcess/new-process.page';
import PasswordProtectedPage from '@pages/PasswordProtected/passwordProtected.page';
import ProcessOverview from '@pages/ProcessOverview';
import AdminSetting from '@pages/Settings/admin-setting';
import Analytics from '@pages/Settings/analytics';
import AnalyticsDetails from '@pages/Settings/analytics/analyticsDetails';
import { GenerationPage } from '@pages/Settings/generation';
import { GenerationDetails } from '@pages/Settings/generation/generationDetails';
import { NewGenerationPage } from '@pages/Settings/generation/NewGenerationPage';
import GlobalVariables from '@pages/Settings/global-variables/globalVariable.page';
import SettingNotifications from '@pages/Settings/notifications';
import EditorNotifications from '@pages/Settings/notifications/editor';
import TraceInfo from '@pages/Settings/trace-info';
import TraceOverview from '@pages/Settings/trace-overview';
import WorkflowAdminSetting from '@pages/Settings/workflow-admin-setting';
import { WorkspacesPage } from '@pages/Workspaces';
import { WorkspaceDetailPage } from '@pages/Workspaces/WorkspaceDetail';
import { FormPanel } from '@panel/components/action/form/form.panel';
import { FORM_ACTION_PATHS_TO_FC_MAP } from '@panel/components/action/form/form.panel.config';
import { TableEditorConfig } from '@panel/components/action/tableEditor/tableEditor.config';
import { TableEditorPanel } from '@panel/components/action/tableEditor/tableEditor.panel';
import { TableImporterConfig } from '@panel/components/action/tableImporter/tableImporter.config';
import { TableImporterPanel } from '@panel/components/action/tableImporter/tableImporter.panel';
import Transition from '@panel/components/transition';
import { TRANSITION_PATHS_TO_FC_MAP } from '@panel/components/transition/transition.config';
import Panel from '@panel/panel.page';
import {
  type Location,
  Navigate,
  Route,
  Routes,
  useLocation
} from 'react-router-dom';
import Layout from '@/layout';
import ProcessLayout from '@/layout/processLayout';
import WorkflowLayout from '@/layout/workflowLayout';
import PageNotFound from '@/pages/PageNotFound/notfound.page';

import { ParamsList } from './routes.types';

const Router = () => {
  const location: Location = useLocation();

  return (
    <Routes location={location}>
      <Route element={<Layout />} errorElement={<PageNotFound />} path="/">
        <Route element={<Home />} errorElement={<PageNotFound />} index />
        <Route
          element={<AddNewProcess />}
          errorElement={<PageNotFound />}
          path="/new"
        />
        <Route
          element={<OrganizationsAdminPage />}
          errorElement={<PageNotFound />}
          path="/admin/organizations"
        />
        <Route
          errorElement={
            <PageNotFound
              description="An error has occured"
              label="take me home"
              title="An error has occured"
            />
          }
          path={`/:${ParamsList.ProcessId}`}
        >
          <Route element={<ProcessLayout />}>
            <Route element={<ProcessOverview />} path="general" />
            <Route
              element={<PasswordProtectedPage />}
              path="password-protected"
            />
            <Route element={<Databases />} path="database">
              <Route element={<Grid />} path={`:${ParamsList.DatabaseId}`} />
            </Route>
            <Route path="workspaces">
              <Route element={<WorkspacesPage />} index />
              <Route
                element={<WorkspaceDetailPage />}
                path={`:${ParamsList.WorkspaceId}`}
              />
            </Route>
            <Route element={<AdminSetting />} path="admin" />
            <Route element={<EnvironmentPage />} path="variables" />
            <Route path="generation">
              <Route element={<GenerationPage />} index />
              <Route element={<NewGenerationPage />} path="new" />
              <Route
                element={<GenerationDetails />}
                path={`:${ParamsList.GenerationId}`}
              />
            </Route>
            <Route path="notification">
              <Route element={<SettingNotifications />} index />
              <Route element={<EditorNotifications />} path={'new'} />
              <Route
                element={<EditorNotifications />}
                path={`:${ParamsList.NotificationId}`}
              />
            </Route>
          </Route>
          <Route path={`:${ParamsList.WorkflowId}`}>
            <Route element={<WorkflowLayout />}>
              <Route
                element={<EditorWrapper />}
                errorElement={
                  <PageNotFound
                    description="An error has occured"
                    label="take me home"
                    title="An error has occured"
                  />
                }
                path="editor"
              >
                <Route element={<Panel />} path="p">
                  <Route
                    element={<FormPanel />}
                    path={`a/:${ParamsList.ActionId}`}
                  >
                    {Object.keys(FORM_ACTION_PATHS_TO_FC_MAP).map(
                      (actionPath: string) => (
                        <Route
                          element={FORM_ACTION_PATHS_TO_FC_MAP[actionPath]}
                          key={actionPath}
                          path={actionPath}
                        />
                      )
                    )}
                  </Route>
                  <Route
                    element={<TableEditorPanel />}
                    path={`te/:${ParamsList.ActionId}`}
                  >
                    {Object.keys(TableEditorConfig.SUBPATHS_TO_FC_MAP).map(
                      (actionPath: string) => (
                        <Route
                          element={
                            TableEditorConfig.SUBPATHS_TO_FC_MAP[
                              actionPath as TableImporterConfig.SubPath
                            ]
                          }
                          key={actionPath}
                          path={actionPath}
                        />
                      )
                    )}
                  </Route>
                  <Route
                    element={<TableImporterPanel />}
                    path={`ti/:${ParamsList.ActionId}`}
                  >
                    {Object.keys(TableImporterConfig.SUBPATHS_TO_FC_MAP).map(
                      actionPath => (
                        <Route
                          element={
                            TableImporterConfig.SUBPATHS_TO_FC_MAP[
                              actionPath as TableImporterConfig.SubPath
                            ]
                          }
                          key={actionPath}
                          path={actionPath}
                        />
                      )
                    )}
                  </Route>

                  <Route
                    element={<Transition />}
                    path={`t/:${ParamsList.TransitionId}`}
                  >
                    {Object.keys(TRANSITION_PATHS_TO_FC_MAP).map(
                      (transitionPath: string) => (
                        <Route
                          element={TRANSITION_PATHS_TO_FC_MAP[transitionPath]}
                          key={transitionPath}
                          path={transitionPath}
                        />
                      )
                    )}
                  </Route>
                </Route>
              </Route>
              <Route element={<WorkflowAdminSetting />} path="admin" />
              <Route element={<TraceInfo />} path="trace-info" />

              <Route element={<TraceOverview />} path="trace-overview" />
              <Route element={<GlobalVariables />} path="global-variables" />
              <Route path="analytics">
                <Route element={<Analytics />} index />
                <Route
                  element={<Navigate replace to=".." />}
                  path={`:${ParamsList.AnalyticsId}`}
                />
                <Route
                  element={<AnalyticsDetails />}
                  path={`:${ParamsList.AnalyticsId}/:${ParamsList.PodId}`}
                />
              </Route>
            </Route>
          </Route>
        </Route>
        <Route element={<PageNotFound />} path="*" />
      </Route>
    </Routes>
  );
};

export default Router;
